import React from "react";
import ProgressBar from "components/ProgressBar";
import CircularProgressBar from "components/CircularProgressBar";
import { Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import MaskedInput from "components/MaskedInput";
import { every, isEmpty, isNull, pick, transform, trim } from "lodash";
import "react-datepicker/dist/react-datepicker.css";
import { connect, type ConnectedProps } from "react-redux";
import { userService } from "services/userService";
import {
  getUserState,
  updateIraAccountIdentityInfo,
} from "actions/userActions";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import Button from "components/Button";
import AddressValidator from "components/AddressValidator";
import Alert from "components/Alert";
import "./IndividualEnroll.scss";
import InfoTooltip from "components/InfoTooltip";
import IconSpinner from "components/IconSpinner";
import IndividualExtraDetails from "./IndividualExtraDetails";
import IndividualVerifyInfo from "./IndividualVerifyInfo";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import StateSelect from "components/StateSelect";
import { userConstants } from "actions/types";
import { userIs401kRollIn } from "../../../store/selectors/user";
import IconSupportCard from "components/IconSupportCard";
import AccountProcedureDisclosure from "components/AccountProcedureDisclosure";
import {
  dobValidator,
  IndividualAddress1Validator,
  individualAddress2Validator,
  NAME_LENGTH_ERROR,
  NAME_MESSAGE,
  noWhitespaceRegex,
  postalCodeValidator,
  PRINTABLE_ASCII_MESSAGE,
  stateValidator,
  testFirstLastName,
  testName,
  testPhoneNumber,
  testPrintableAsciiCharacters,
  testSsn,
  INVALID_SSN_ERROR,
  validateUsersAddress,
  whiteSpaceError,
  ALPHANUMERIC_SPACES_MESSAGE,
  testAlphanumericSpaces,
  INVALID_PHONE_NUMBER,
} from "utils/fieldValidators";
import { scrollToTop } from "utils/dom";
import { PERSONAL_INFO } from "statics/onboardingSteps";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

/* eslint-disable react/no-children-prop */

import * as yup from "yup";

/** The identity fields tracked in component state and submitted to the API. */
export interface IdentityValues {
  firstName: string;
  lastName: string;
  legalName: string;
  dob: string;
  ssn: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
}

/** Formik form values: the identity fields plus the SSN confirmation field. */
type EnrollFormValues = IdentityValues & { confirmSsn: string };

const schema = yup.object({
  firstName: yup
    .string()
    .label("First")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .required()
    .test("is-valid-name", NAME_MESSAGE, testName)
    .min(1, "Must be at least one character.")
    .max(20)
    .test("first-last-name-length", NAME_LENGTH_ERROR, testFirstLastName),
  lastName: yup
    .string()
    .label("Last")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .required()
    .test("is-valid-name", NAME_MESSAGE, testName)
    .min(1, "Must be at least one character.")
    .max(20, "Must be less than 20 characters.")
    .test("first-last-name-length", NAME_LENGTH_ERROR, testFirstLastName),
  legalName: yup
    .string()
    .label("Legal Name")
    .matches(noWhitespaceRegex, whiteSpaceError)
    .optional()
    .test("is-valid-legal-name", NAME_MESSAGE, testName)
    .max(30),
  dob: dobValidator,

  ssn: yup
    .string()
    .required()
    .label("Social Security Number")
    // PRESERVED BUG (conversion-only): testSsn is passed directly, so yup hands
    // its TestContext to the truthy `allowNoDash` parameter, silently loosening
    // the SSN format. Locked by characterization tests — keep verbatim (do NOT
    // wrap in a lambda the way Beneficiaries does). @ts-expect-error: the extra
    // boolean param is incompatible with yup's TestFunction context arg.
    // @ts-expect-error preserved-bug arg-shift; see comment above
    .test("ssn", INVALID_SSN_ERROR, testSsn),
  confirmSsn: yup
    .string()
    .required()
    .label("Confirm Social Security Number")
    .test("ssn-match", "Social Security Numbers must match", function (value) {
      return this.parent.ssn === value;
    }),
  address1: IndividualAddress1Validator,
  address2: individualAddress2Validator,
  city: yup
    .string()
    .label("City")
    .required()
    .test("city", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("city", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
    .max(50)
    .matches(noWhitespaceRegex, whiteSpaceError),
  state: stateValidator,
  postalCode: postalCodeValidator,
  phone: yup
    .string()
    .label("Phone")
    .required()
    .test("phone", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("us-phone", INVALID_PHONE_NUMBER, testPhoneNumber)
    .max(15),
});

const verifyIdentity = (identity: IdentityValues) => {
  const userIdentity = pick(identity, [
    "firstName",
    "lastName",
    "dob",
    "address1",
    "city",
    "state",
    "postalCode",
    "phone",
    "ssn",
  ]);
  return every(userIdentity, (val) => !isNull(val) && !isEmpty(val));
};

const loadingSelector = createLoadingSelector([
  userConstants.USER_ON_BOARDING_IDENTITY_UPDATE,
]);

const errorSelector = createErrorSelector([
  userConstants.USER_ON_BOARDING_IDENTITY_UPDATE,
]);

const mapStateToProps = (state: RootState) => ({
  // selectors/user userState slice is `object` in RootState — the legacy
  // `.state` field is read directly here, so narrow at the boundary.
  userState: (state.user.userState as { state: string }).state,
  error: errorSelector(state),
  isFetching: loadingSelector(state),
  isPaychex: userIs401kRollIn(state),
});

const mapDispatchToProps = {
  updateIraAccountIdentityInfo,
  getUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  identity: IdentityValues;
  submitted: boolean;
  showAddressModal: boolean;
  loading: boolean;
  shouldVerifyData: boolean;
  showLastEnrollmentPage: boolean;
  fetchingAddress: boolean;
  confirmSsn: string;
  // set only when address validation finds a mismatch; read in render +
  // passed to AddressValidator. Not part of the initial state shape.
  validatedAddress?: Awaited<
    ReturnType<typeof userService.getValidatedAddress>
  >;
}

class IndividualEnroll extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      identity: {
        firstName: "",
        lastName: "",
        legalName: "",
        dob: "",
        ssn: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        phone: "",
      },
      submitted: false,
      showAddressModal: false,
      loading: true,
      shouldVerifyData: false,
      showLastEnrollmentPage: false,
      fetchingAddress: false,
      confirmSsn: "",
    };

    userService
      .getIraAccountForm(this.props.client as ApolloClient<object>)
      .then(
        (data) => {
          // data.identity fields are `string | null` from the query; the
          // original assigns them straight onto state.identity, so cast to
          // match the IdentityValues shape without altering runtime values.
          let identity = {
            firstName: data.identity!.firstName,
            lastName: data.identity!.lastName,
            legalName: data.identity!.legalName,
            address1: data.identity!.address1,
            address2: data.identity!.address2,
            city: data.identity!.city,
            state: data.identity!.state,
            postalCode: data.identity!.postalCode,
            phone: data.identity!.phone,
            dob: data.identity!.dob,
            ssn: data.identity!.ssn,
          } as IdentityValues;

          const identityIsVerified = verifyIdentity(identity);

          if (this._isMounted) {
            this.setState({
              submitted: false,
              identity,
              loading: false,
              shouldVerifyData: identityIsVerified,
            });
          }
        },
        () => {
          if (this._isMounted) {
            this.setState({
              submitted: false,
              loading: false,
            });
          }
        }
      );
  }

  componentDidMount() {
    this._isMounted = true;
    scrollToTop();
    window.analytics.page("Individual Information");
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  submitIndividual = async (
    values: IdentityValues,
    skipAddressValidation = false
  ) => {
    // verify user address, if it matches USPS just continue on
    // if USPS endpoint fails continue on
    // if there is a mismatch then open modal to allow customer to decide which to use
    if (!skipAddressValidation) {
      try {
        this.setState({
          fetchingAddress: true,
        });

        const userAddress = {
          address1: values.address1,
          address2: values.address2,
          state: values.state,
          postalCode: values.postalCode,
          city: values.city,
        };

        const validatedAddress = await userService.getValidatedAddress(
          this.props.client as ApolloClient<object>,
          userAddress
        );
        this.setState({
          fetchingAddress: false,
        });

        const userAddressIsValid = validateUsersAddress(
          userAddress,
          // validatedAddress is the nullable query result; validateUsersAddress
          // (already-typed util) expects AddressFields. Original passed it
          // straight — cast at the boundary without altering runtime values.
          validatedAddress as Parameters<typeof validateUsersAddress>[1]
        );

        if (!userAddressIsValid) {
          this.setState({
            showAddressModal: true,
            validatedAddress,
          });

          return;
        }
      } catch (error) {
        // if the validation endpoint fails we dont want to prevent user from saving so continue on and log error
        console.log(error);
      }
    }

    let identity = {
      firstName: values.firstName,
      lastName: values.lastName,
      legalName: values.legalName,
      dob: values.dob,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      phone: values.phone,
      ssn: values.ssn,
    };

    void this.props
      .updateIraAccountIdentityInfo(
        this.props.client as ApolloClient<object>,
        identity
      )
      .then(() => {
        void this.props.getUserState(this.props.client as ApolloClient<object>);
        if (!this.props.error) {
          this.setState({
            showLastEnrollmentPage: true,
            shouldVerifyData: false,
          });
          // if there was an error we want to make sure we are showing the info page w/ error
        } else {
          this.setState({
            showAddressModal: false,
            shouldVerifyData: false,
            showLastEnrollmentPage: false,
          });
        }
      });

    window.analytics.track("Submitted Individual Information", {
      userState: this.props.userState,
    });

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  _onEditAddress = () => {
    this.setState({
      showAddressModal: false,
      shouldVerifyData: false,
      showLastEnrollmentPage: false,
    });
  };

  _closeModal = (shouldShowEditMode: boolean = false) => {
    if (shouldShowEditMode) {
      this._onEditAddress();
      return;
    }
    this.setState({
      showAddressModal: false,
    });
  };

  _renderIndividualForm = () => {
    const { identity } = this.state;

    return (
      <Formik<EnrollFormValues>
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={(values) => {
          this.setState({ identity: values });
          void this.submitIndividual(
            transform(
              values,
              (result: Record<string, string>, value, key) =>
                (result[key] = trim(value))
            ) as unknown as IdentityValues
          );
        }}
        enableReinitialize={false}
        initialValues={{
          ...identity,
          confirmSsn: "",
        }}
      >
        {({
          handleSubmit,
          handleChange,
          handleBlur,
          values,
          touched,
          errors,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <ScrollToFieldError />
            <div className="individual-enroll-layout">
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="30"
                >
                  <section className="page-title-wrap">
                    <article className="text-cell">
                      <p className="page-eyebrow">PERSONAL INFO</p>
                      <p className="page-title">Tell us about yourself.</p>
                    </article>
                    <article className="progress-cell">
                      <ul className="circular-progress-wrap">
                        <CircularProgressBar
                          strokeWidth="8"
                          sqSize="75"
                          percentage="30"
                        />
                      </ul>
                    </article>
                  </section>
                  <div id="form-individual-info">
                    <section className="form-sec-2col">
                      <article className="col-form">
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicFirstName">
                            <Form.Label>Legal First Name</Form.Label>
                            <Form.Control
                              name="firstName"
                              placeholder="Legal first name"
                              value={values.firstName}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={
                                touched.firstName && !!errors.firstName
                              }
                              isValid={touched.firstName && !errors.firstName}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.firstName}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group as={Col} controlId="formBasicLastName">
                            <Form.Label>Legal Last Name</Form.Label>
                            <Form.Control
                              name="lastName"
                              placeholder="Legal last name"
                              value={values.lastName}
                              onChange={handleChange}
                              isInvalid={touched.lastName && !!errors.lastName}
                              isValid={touched.lastName && !errors.lastName}
                              onBlur={handleBlur}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.lastName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicSsnField">
                            <Form.Label>
                              Social Security Number{" "}
                              <InfoTooltip tooltipBody="Your Social Security number helps us verify your identity and esnsure we're creating a bank account that follows all government regulations." />
                            </Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="000-00-0000"
                              name="ssn"
                              placeholder="000-00-0000"
                              value={values.ssn}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.ssn && !!errors.ssn}
                              isValid={touched.ssn && !errors.ssn}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.ssn}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            controlId="formBasicSsnConfirmField"
                          >
                            <Form.Label>
                              Confirm Social Security Number
                            </Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="000-00-0000"
                              name="confirmSsn"
                              placeholder="000-00-0000"
                              value={values.confirmSsn}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={
                                touched.confirmSsn && !!errors.confirmSsn
                              }
                              isValid={touched.confirmSsn && !errors.confirmSsn}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.confirmSsn}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>

                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            md={4}
                            controlId="formBasicDateOfBirth"
                          >
                            <Form.Label>Date of Birth</Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="00/00/0000"
                              name="dob"
                              placeholder="MM/DD/YYYY"
                              value={values.dob}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.dob && !!errors.dob}
                              isValid={touched.dob && !errors.dob}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.dob}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Form.Label>
                          Home Address{" "}
                          <InfoTooltip tooltipBody="This is used for identity verification, so please use your permanent residential address P.O. boxes will be rejected." />
                        </Form.Label>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicaddress1"
                        >
                          <Form.Control
                            name="address1"
                            placeholder="Address 1"
                            value={values.address1}
                            onChange={handleChange}
                            isInvalid={touched.address1 && !!errors.address1}
                            isValid={touched.address1 && !errors.address1}
                            onBlur={handleBlur}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address1}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicaddress2"
                        >
                          <Form.Control
                            name="address2"
                            placeholder="Address 2"
                            value={values.address2}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={touched.address2 && !!errors.address2}
                            isValid={touched.address2 && !errors.address2}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address2}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicCity">
                            <Form.Control
                              name="city"
                              placeholder="City"
                              value={values.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.city && !!errors.city}
                              isValid={touched.city && !errors.city}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.city}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group
                            as={Col}
                            sm={4}
                            controlId="formBasicState"
                          >
                            <StateSelect
                              value={values.state}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.state}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group
                            as={Col}
                            sm={3}
                            controlId="formBasicPostalCode"
                          >
                            <Form.Control
                              name="postalCode"
                              placeholder="Zip"
                              value={values.postalCode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.postalCode && !!errors.postalCode
                              }
                              isValid={touched.postalCode && !errors.postalCode}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.postalCode}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={5}
                            controlId="formBasicPhone"
                          >
                            <Form.Control
                              as={MaskedInput}
                              mask="(000) 000-0000"
                              name="phone"
                              placeholder="Phone"
                              value={values.phone}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.phone && !!errors.phone}
                              isValid={touched.phone && !errors.phone}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.phone}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <div
                          className="submit-row desktop-only"
                          style={{ marginTop: 24 }}
                        >
                          {this.props.error && (
                            <Alert type="error" msg={this.props.error} />
                          )}
                          <Button
                            name="submit"
                            withArrow={true}
                            loading={
                              this.props.isFetching ||
                              this.state.fetchingAddress
                            }
                          />
                        </div>
                      </article>
                    </section>
                  </div>
                </div>
              </div>
              <div className="individual-enroll-sidebar">
                <AccountProcedureDisclosure />
                <IconSupportCard />
              </div>
              <div className="submit-row mobile-only">
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
                <Button
                  name="submit"
                  withArrow={true}
                  loading={this.props.isFetching || this.state.fetchingAddress}
                />
              </div>
            </div>
          </Form>
        )}
      </Formik>
    );
  };

  render() {
    let content: React.ReactNode;

    if (this.state.loading) {
      content = <IconSpinner centered />;
    } else if (this.state.shouldVerifyData) {
      content = (
        <IndividualVerifyInfo
          identity={this.state.identity}
          submitIndividual={this.submitIndividual}
          onEditAddress={this._onEditAddress}
          isFetching={this.props.isFetching || this.state.fetchingAddress}
        />
      );
    } else if (this.state.showLastEnrollmentPage) {
      content = <IndividualExtraDetails />;
    } else {
      content = this._renderIndividualForm();
    }
    return (
      <div className="individual-enroll">
        {this.state.showAddressModal && (
          <AddressValidator
            show={this.state.showAddressModal}
            // AddressValidator (already-typed sibling) declares onSubmit with the
            // broader AddressValidatorAddress param; submitIndividual takes the
            // all-required IdentityValues. At runtime AddressValidator always
            // forwards the full identity, so cast at this boundary.
            onSubmit={
              this.submitIndividual as unknown as React.ComponentProps<
                typeof AddressValidator
              >["onSubmit"]
            }
            // state.validatedAddress holds the raw query result (nullable fields);
            // cast to the component's address shape at the boundary.
            validatedAddress={
              this.state.validatedAddress as React.ComponentProps<
                typeof AddressValidator
              >["validatedAddress"]
            }
            onClose={this._closeModal}
            // IdentityValues has no index signature; AddressValidatorAddress
            // (already-typed sibling) declares one. Cast at the boundary.
            values={
              this.state.identity as unknown as React.ComponentProps<
                typeof AddressValidator
              >["values"]
            }
          />
        )}
        <ProgressBar
          isEmployer={false}
          activeStepId={PERSONAL_INFO.id}
          progressPercent={"40"}
        />
        <div>{content}</div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(IndividualEnroll));
